import React, { useState } from 'react';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  FUNCTION_MODE,
  ManageChiefSupervisor,
} from './ManageChiefSupervisor/ManageChiefSupervisor';
import { CommenceOfStudy } from './CommenceOfStudy';
import { FUNCTION_MODULE } from './const';
import { ExtendPaymentDeadline } from './ExtendPaymentDeadline';
import { FUNCTION_HUB_TEXT } from './lang';
import {
  changeSupervisorFilter,
  commenceOfStudyFilter,
  declineSupervisorFilter,
  extendRegistrationFeeFilter,
  rejectApplicationFilter,
} from '../filters';
import { Button, ButtonContainer } from '../../../../../components';
import { useAuth } from '../../../../../contexts';

type ActivatedModule = {
  title: string;
  component: React.ReactNode;
};

type FunctionHubProps = {
  departmentLevel?: boolean;
};
const modulesDisabledMap: Record<FUNCTION_MODULE, boolean> = {
  [FUNCTION_MODULE.EXTEND_REGISTRATION_FEE]: false,
  [FUNCTION_MODULE.COMMENCE_OF_STUDY]: false,
  [FUNCTION_MODULE.CHANGE_SUPERVISOR]: false,
  [FUNCTION_MODULE.REJECT_APPLICATION]: false,
  [FUNCTION_MODULE.DECLINE_APPLICATION]: true,
};

const getInitModuleKey = (): FUNCTION_MODULE => {
  const searchParams = new URLSearchParams(location.search);
  const defaultFmKey = FUNCTION_MODULE.EXTEND_REGISTRATION_FEE;
  const urlFmKey = searchParams.get('fm') as FUNCTION_MODULE;
  if (modulesDisabledMap[urlFmKey]) return defaultFmKey;

  return urlFmKey ?? defaultFmKey;
};
export const FunctionHub: React.FC<FunctionHubProps> = (
  props: FunctionHubProps,
) => {
  const { departmentLevel = false } = props;
  const { user } = useAuth();
  const navigate = useNavigate();
  const initFmKey = getInitModuleKey();
  const [activatedModuleKey, setActivatedModuleKey] =
    useState<FUNCTION_MODULE>(initFmKey);

  const modulesMap: Record<FUNCTION_MODULE, ActivatedModule> = {
    [FUNCTION_MODULE.EXTEND_REGISTRATION_FEE]: {
      title: FUNCTION_HUB_TEXT.EXTEND_REGISTRATION_FEE.TITLE,
      component: (
        <ExtendPaymentDeadline
          key={FUNCTION_MODULE.EXTEND_REGISTRATION_FEE}
          filter={extendRegistrationFeeFilter(user, departmentLevel)}
          departmentLevel={departmentLevel}
        />
      ),
    },
    [FUNCTION_MODULE.COMMENCE_OF_STUDY]: {
      title: FUNCTION_HUB_TEXT.COMMENCE_OF_STUDY.TITLE,
      component: (
        <CommenceOfStudy
          key={FUNCTION_MODULE.COMMENCE_OF_STUDY}
          departmentLevel={departmentLevel}
          filter={commenceOfStudyFilter(user, departmentLevel)}
        />
      ),
    },
    // Change Chief Supervisor & Reject Application use the same initial filter
    [FUNCTION_MODULE.CHANGE_SUPERVISOR]: {
      title: FUNCTION_HUB_TEXT.CHANGE_SUPERVISOR.TITLE,
      component: (
        <ManageChiefSupervisor
          key={FUNCTION_MODE.CHANGE_SUPERVISOR}
          filter={changeSupervisorFilter(user, departmentLevel)}
          departmentLevel={departmentLevel}
          functionMode={FUNCTION_MODE.CHANGE_SUPERVISOR}
        />
      ),
    },

    [FUNCTION_MODULE.REJECT_APPLICATION]: {
      title: FUNCTION_HUB_TEXT.REJECT_APPLICATION.TITLE,
      component: (
        <ManageChiefSupervisor
          key={FUNCTION_MODE.REJECT_APPLICATION}
          filter={rejectApplicationFilter(user, departmentLevel)}
          departmentLevel={departmentLevel}
          functionMode={FUNCTION_MODE.REJECT_APPLICATION}
        />
      ),
    },
    [FUNCTION_MODULE.DECLINE_APPLICATION]: {
      title: FUNCTION_HUB_TEXT.DECLINE_APPLICATION.TITLE,
      component: (
        <ManageChiefSupervisor
          key={FUNCTION_MODE.DECLINE_APPLICATION}
          filter={declineSupervisorFilter(user, departmentLevel)}
          departmentLevel={departmentLevel}
          functionMode={FUNCTION_MODE.DECLINE_APPLICATION}
        />
      ),
    },
  };

  const handleChangeActivated = (key: FUNCTION_MODULE): void => {
    setActivatedModuleKey(key);
    handleSetUrl(key);
  };

  const handleSetUrl = (key: FUNCTION_MODULE): void => {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('fm', key);
    navigate(`${currentUrl.pathname}${currentUrl.search}`);
  };

  const buttonList = Object.keys(FUNCTION_MODULE).map((key, index) => {
    const selected = key === activatedModuleKey;
    const { title } = modulesMap[key as FUNCTION_MODULE];
    // hide disabled tabs
    if (modulesDisabledMap[key as FUNCTION_MODULE]) return <></>;
    return (
      <Button
        key={index}
        onClick={() => {
          handleChangeActivated(key as FUNCTION_MODULE);
        }}
        disabled={modulesDisabledMap[key as FUNCTION_MODULE]}
        variant={selected ? 'contained' : 'outlined'}
        className="!normal-case !p-4 !my-2 w-[200px] h-[80px]"
      >
        {title}
      </Button>
    );
  });

  const moduleComponent = modulesMap[activatedModuleKey]?.component;

  return (
    <>
      <ButtonContainer flexWrap={'wrap'} spacing={2}>
        {buttonList}
      </ButtonContainer>
      <Box className="py-4">{moduleComponent}</Box>
    </>
  );
};
